import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { resendVerification } from "../../services/AuthServices";
import styles from './styles/LoginRegister.module.css';

const STATUS_MESSAGES = {
    invalid: {
        heading: "Verification link invalid",
        body: "This verification link is invalid or has already been used. If your account isn't verified yet, request a new link below.",
    },
    expired: {
        heading: "Verification link expired",
        body: "Your verification link has expired. Enter your email below and we'll send you a fresh one.",
    },
    error: {
        heading: "Something went wrong",
        body: "We couldn't verify your account just now. You can request a new verification link below, or try again later.",
    },
};

/**
 * Dual-purpose screen:
 *  - Rendered inside AuthModal / FrontPage (no query params) as the post-registration
 *    "check your email" prompt.
 *  - Rendered at the /verify-email route when the backend redirects here after a FAILED
 *    verification (?status=invalid | expired | error). On SUCCESS the backend verifies,
 *    sets the session cookie, and redirects straight to /home?verified=true, so the happy
 *    path never reaches this component.
 *
 * In every case a resend form is offered so a user with a dead/missing link can recover.
 */
function VerifyEmail() {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get('status');

    const [email, setEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const [resendError, setResendError] = useState('');

    const message = STATUS_MESSAGES[status] || {
        heading: "Verify your email",
        body: "Please check your email for a verification link. If you don't see it, check your spam folder — or request a new one below.",
    };

    const handleLoginNavigation = () => {
        navigate('/', {
            state: {
                showModal: true,
                authModalState: 'login',
                ...(location.state?.acceptInvite ? { acceptInvite: true } : {}),
            },
        });
    };

    const handleResend = async (e) => {
        e.preventDefault();
        setResendLoading(true);
        setResendError('');
        setResendMessage('');
        try {
            const data = await resendVerification(email.trim());
            setResendMessage(data.message || 'If an unverified account exists for that email, a new verification link has been sent.');
        } catch (err) {
            setResendError(err.response?.data?.errorMessage || 'Could not resend the verification email. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.h1}>{message.heading}</h1>
            <p>{message.body}</p>

            <form onSubmit={handleResend} className={styles.form}>
                <label className={styles.label}>Email</label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className={styles.input}
                />
                {resendError && <div className={styles.error}>{resendError}</div>}
                {resendMessage && <div className={styles.success}>{resendMessage}</div>}
                <button type="submit" className={styles.button} disabled={resendLoading}>
                    {resendLoading ? 'Sending...' : 'Resend verification email'}
                </button>
            </form>

            <div className={styles.links}>
                Already verified?{' '}
                <span onClick={handleLoginNavigation} className={styles.linkSpan}>
                    Log in
                </span>
            </div>
        </div>
    );
}

export default VerifyEmail;
